import * as _ from 'lodash'
import Cookies from 'js-cookie'
import request from '@/modules/base/util/request'
import BaseModel from 'base/model/base_model'
import settingsConfig from 'settings/config'
import accountConfig from '@/modules/account/config'
import WebStorageService from '_services/webstorage.service'
import WebStorageKey from 'enums/web-storage-key.enum'
import { filterVatListByDateAndCountry, vatDatePeriodisValid } from '@/modules/transactionV2/util/vat'
import globalConfig from '@/config'
import initPendo from '@/modules/_helpers/initPendo'
import { identifyPostHogUser, resetPostHogUser } from '@/modules/posthog/posthog-tracking'
import reduxStore from '@/redux/store'
import { setEmailServiceStatus, updateImapEmails, cleanupImapEvents } from '@/redux/ducks/email/status'

import { updateData as updateTenantData, fetchData as fetchTenantData, TENANT_INFO_KEYS } from '@/redux/ducks/tenant/info'
import { updateData as updateTenantSetting, fetchData as fetchTenantSetting, resetDmsTokenSession } from '@/redux/ducks/tenant/settings'
import { updateData as updateUserData } from '@/redux/ducks/user/info'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { MenuPositions, MenuTypes } from '@/modules/layout/constants'
import { subscriptionTypes } from '@/modules/base/util/constants'
import { isMeinBuero } from '@/utils/build-env'
import CelloService from '@/modules/cello/services/cello.service'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { resetFiltersForAllLists } from '@/redux/ducks/lists'
import { getPostLoginRedirectDecision, isNonReturnableRoute, RedirectContext } from '@/utils/post-login-redirect'
import { fetchTenantUserSettings, setSettings as updateTenantUserSettings } from '@/redux/ducks/tenantUser/settings'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import {
  BOOTSTRAP_CEILING_MS,
  captureBootstrapError,
  runBootstrapSteps,
  withTimeout,
} from './login-bootstrap'

const isMeinbuero = isMeinBuero()
const { DOCUMENT_S, DOCUMENT_M, DOCUMENT_XL, DOCUMENT_L, DOCUMENT_TRIAL } = subscriptionTypes

const defaultUserSettings = { displayVatNotification: true }

export default BaseModel.extend({
  idAttribute: 'id',

  props: {
    id: ['string'],
    token: ['string', true],
    isSmallBusiness: ['boolean', true, false],
    isDemo: ['boolean', true, false],
    allSubscriptionsLocked: ['boolean', true, false],
    subscriptions: ['array'],
    loggedIn: ['boolean', true, false],
    vatCodes: ['array'],
    vatCodeList: ['array'],
    roles: ['object'],
    subscriptionType: ['string', false, 'trial'],
    sessionSettings: ['object', true, () => {
      return {}
    }],
    registrationClient: ['string', true, 'web'],
    registeredAt: ['string', true],
    bankingLogsEnabled: ['boolean', false, false],
    isDesktopTenant: ['boolean', false, false],
    sessionFromDesktop: ['boolean', false, false],
    isDesktopConnected: ['boolean', false, false],
    showPaypalAd: ['boolean', false, false],
    showPaypalLinkSuccess: ['boolean', false, false],
    gaClientIdNeeded: ['boolean', true, true],
    isAdminLogin: ['boolean', false, false],
    recurringInvoiceCount: ['number'],
    articleData: ['array'],
    experimentalFeatures: ['array'],
    userId: ['string'],
    layoutSettings: ['object', false, () => ({
      darkMode: false,
      menu: { position: MenuPositions.left, type: MenuTypes.comfortable },
      ...(isMeinbuero ? {} : { showUnavailableFeatures: false }),
    })],
    tenantUserSettings: ['object', false, () => ({
      ai: {
        enabled: true,
      },
    })],
  },

  // session (not props): toJSON() excludes session properties, so this flag can never be
  // persisted to local storage by store(). A persisted `true` would lock every protected
  // route behind a loader after a reload during login.
  session: {
    loginInProgress: ['boolean', true, false],
  },

  constructor: function UserModel(attrs) {
    attrs = attrs || WebStorageService.getItem(WebStorageKey.USER)
    WebStorageService.setItem(WebStorageKey.IS_LOCKED_CONFIRMED, false)
    BaseModel.call(this, attrs)
    if (this.token) {
      this.setDataFromJwt()
    }
  },

  initialize: function() {
    this.on('change', this.store.bind(this))
    // defensive reset: never inherit the flag from a previously stored user object
    this.loginInProgress = false
    if (this.token && !this._loginPromise && !this._initialFetchDone) {
      this._initialFetchDone = true
      queueMicrotask(() => this.fetchData())
    }
  },

  _loginInProgressTimer: null,

  /**
   * Single choke point for the loginInProgress flag: keeps the model and the redux store in
   * sync (request.js and the model read the model, components read redux) and guarantees via
   * a watchdog that the flag can never outlive one bootstrap window - otherwise
   * protected-route would render a loader forever.
   */
  setLoginInProgress: function(value) {
    const next = Boolean(value)
    const store = require('@/redux/store').default

    clearTimeout(this._loginInProgressTimer)
    this._loginInProgressTimer = null

    this.set({ loginInProgress: next })
    store.dispatch(updateUserData({ loginInProgress: next }))

    if (next) {
      this._loginInProgressTimer = setTimeout(() => {
        captureBootstrapError('loginInProgress-watchdog', new Error('flag was never cleared'))
        this.setLoginInProgress(false)
      }, BOOTSTRAP_CEILING_MS * 2)
    }
  },

  fetchData: function() {
    const store = require('@/redux/store').default
    store.dispatch(fetchTenantData())
    store.dispatch(fetchTenantSetting())
    store.dispatch(fetchTenantUserSettings())
    updateSubscriptionDetails(true, true)
  },

  store: function() {
    // exclude bankingLogsEnabled prop from persisted state in local storage
    const { bankingLogsEnabled, ...storedUserProps } = this.toJSON()
    WebStorageService.setItem(WebStorageKey.USER, storedUserProps)
    WebStorageService.setItem(WebStorageKey.USER_SETTINGS, { ...defaultUserSettings })
  },

  setTenantInfo: function(infos, updateStore = true) {
    const toAssign = {}
    if (infos.subscriptions?.length) {
      toAssign.allSubscriptionsLocked = !infos.subscriptions?.find(s => s.isSubscriptionLocked === false)
    }
    const data = Object.assign({}, this.props, infos, toAssign)
    this.set(data)
    if (updateStore) {
      const store = require('@/redux/store').default
      store.dispatch(updateTenantData(data))
    }
  },

  setTenantSetting: function(settings, updateStore = true, dmsTokenSessionEpoch = undefined) {
    const store = updateStore ? require('@/redux/store').default : null
    const currentSessionEpoch = store?.getState().tenant.settings.dmsTokenSessionEpoch ?? 0
    const sourceEpoch = dmsTokenSessionEpoch ?? currentSessionEpoch
    if (updateStore && sourceEpoch !== currentSessionEpoch) return

    const data = { ...this.props, ...settings }
    this.set(data)
    if (updateStore) {
      store.dispatch(updateTenantSetting(data, sourceEpoch))
    }
  },

  setTenantUserSetting: function(settings, updateStore = true) {
    const data = { ...this.props, ...settings }
    this.set(data)
    if (updateStore) {
      const store = require('@/redux/store').default
      store.dispatch(updateTenantUserSettings(data))
    }
  },

  setUserInfo: async function(infos, updateStore = true) {
    const data = Object.assign({}, this.props, infos)
    this.set(data)
    if (updateStore) {
      const store = require('@/redux/store').default
      store.dispatch(updateUserData(data))
    }
  },

  setDataFromJwt: function() {
    const jwtParts = this.token.split('.')
    const {
      bankingLogsEnabled,
      experimentalFeatures,
      isDesktopTenant,
      user: userId,
      sessionFromDesktop,
    } = {
      bankingLogsEnabled: false,
      isDesktopTenant: false,
      sessionFromDesktop: false,
      ...JSON.parse(Buffer.from(jwtParts[1], 'base64')),
    }

    const jwtUserData = {
      bankingLogsEnabled,
      experimentalFeatures,
      isDesktopTenant,
      sessionFromDesktop,
      userId,
    }

    this.set(jwtUserData)

    const store = require('@/redux/store').default
    store.dispatch(updateUserData(jwtUserData))

    return jwtUserData
  },

  initPendo: function() {
    if (this.token && !this.loginInProgress) {
      request(accountConfig.endpoints.trackingInformation, { auth: true, isConcurrent: true, skipLogout: true })
        .then(response => {
          const { body: { data: trackingData } } = response
          initPendo(this.get('id'), trackingData)
        })
        .catch((error) => {
          if (error.response.status === 401) {
            // swallow this error because under some circumstances this function is called with an old invalid token
          }
        })
    }
  },

  initPostHog: function() {
    if (this.token && !this.loginInProgress) {
      request(accountConfig.endpoints.trackingInformation, { auth: true, isConcurrent: true, skipLogout: true })
        .then(response => {
          const { body: { data: trackingData } } = response
          identifyPostHogUser(trackingData)
        })
        .catch((error) => {
          if (error.response.status === 401) {
            // swallow this error because under some circumstances this function is called with an old invalid token
          }
        })
    }
  },

  getUserInfo: async function() {
    return request(settingsConfig.getUserInfo, { auth: true, isConcurrent: true })
  },

  getTenantInfo: async function() {
    return request(settingsConfig.getTenantInfo, { auth: true, isConcurrent: true })
  },

  getTenantSetting: async function() {
    return request(settingsConfig.getTenantSetting, { auth: true, isConcurrent: true })
  },

  getTenantUserSetting: async function() {
    return request(settingsConfig.tenantUserSettings, { auth: true, isConcurrent: true })
  },

  switch: function(attrs) {
    window.localStorage.removeItem(WebStorageKey.SHOPPING_CART)
    resetPostHogUser()
    return this.login(attrs)
  },

  _loginPromise: null,

  login: function(attrs, opts) {
    if (this._loginPromise) {
      return this._loginPromise
    }
    const DOCUMENT_TYPES = [DOCUMENT_S, DOCUMENT_M, DOCUMENT_XL, DOCUMENT_L, DOCUMENT_TRIAL]
    let isOnlyDocumentSub = false
    const pendingReturnUrl = WebStorageService.getItem(WebStorageKey.RETURN_AFTER_LOGIN)
    const pendingReturnIsShop = typeof pendingReturnUrl === 'string' && pendingReturnUrl.startsWith('/shop')

    const returnUrl = WebStorageService.getItem(WebStorageKey.RETURN_AFTER_LOGIN)
    // we force returnUrl if restore is given
    if (!/restore\/.+/.test(returnUrl)) {
      if (isMeinbuero) {
        if (attrs?.data?.activeTenant?.subscriptions?.length === 1) {
          isOnlyDocumentSub = DOCUMENT_TYPES.includes(attrs.data.activeTenant.subscriptions[0].name)
          if (!pendingReturnIsShop) {
            WebStorageService.removeItem(WebStorageKey.RETURN_AFTER_LOGIN)
          }
        }
      }
      if (attrs?.data?.subscriptions?.length === 1) {
        isOnlyDocumentSub = DOCUMENT_TYPES.includes(attrs.data.subscriptions[0])
        if (!pendingReturnIsShop) {
          WebStorageService.removeItem(WebStorageKey.RETURN_AFTER_LOGIN)
        }
      }
    }
    WebStorageService.setItem(WebStorageKey.IS_LOCKED_CONFIRMED, false)

    opts = opts || {}
    opts = _.defaults(opts, {
      path: isOnlyDocumentSub ? '/documents' : '/',
      noRedirect: false,
      isOnlyDocumentSub,
    })

    if (attrs?.data?.linkShopError) {
      WebStorageService.setItem(WebStorageKey.SHOP_ERROR, attrs.data.linkShopError)
      if (attrs?.data?.redirectUrl) {
        const ok = window.open(attrs.data.redirectUrl, '_blank')
        if (!ok) {
          window.location.href = attrs.data.redirectUrl
        }
      }
    } else if (attrs?.data?.redirectUrl) {
      window.location.href = attrs.data.redirectUrl
    }

    const store = require('@/redux/store').default

    let loginDmsTokenSessionEpoch

    const loginUser = () => {
      window.localStorage.removeItem(WebStorageKey.HIDE_X_INVOICE_MODAL)
      window.localStorage.removeItem(WebStorageKey.READ_ONLY_CONFIRMED)
      window.localStorage.removeItem(WebStorageKey.DANGLING_SUBSCRIPTION_LATER)
      window.localStorage.removeItem(WebStorageKey.OPEN_DASHBOARD_CLIENT_INVITATIONS)
      window.localStorage.removeItem(WebStorageKey.NO_ROLE_ASSIGNED_CONFIRMED)
      window.localStorage.removeItem(WebStorageKey.OVERRIDE_MOBILE_WARNING)
      const loginData = {
        ...(attrs.data.id && { id: attrs.data.id }),
        ...(attrs.data.email && { email: attrs.data.email }),
        token: attrs.data.token,
        loggedIn: true,
        registrationClient: attrs.data.registrationClient || 'web',
        registeredAt: attrs.data.registeredAt,
        subscriptionType: attrs.data.subscription,
      }
      // set the flag first: subscribers must never observe loggedIn without loginInProgress,
      // otherwise a protected route renders before the bootstrap data is there
      this.setLoginInProgress(true)
      this.set(loginData)
      store.dispatch(updateUserData(loginData))
      store.dispatch(resetDmsTokenSession())
      loginDmsTokenSessionEpoch = store.getState().tenant.settings.dmsTokenSessionEpoch ?? 0

      this.setDataFromJwt()
    }

    const setTenantInfo = (response) => {
      const data = _.pick(response.body.data, ['isDemo', 'isSmallBusiness', 'vatCodeList', 'vatCodes', ...TENANT_INFO_KEYS])
      return this.setTenantInfo(data, true)
    }

    const setTenantSetting = (response) => {
      return this.setTenantSetting(response.body.data, true, loginDmsTokenSessionEpoch)
    }

    const setTenantUserSetting = (response) => {
      return this.setTenantUserSetting(response.body.data)
    }

    const getTrackingData = () => {
      return request(accountConfig.endpoints.trackingInformation, { auth: true, isConcurrent: true })
    }

    const sendTrackingDataToPendo = response => {
      const { body: { data: trackingData } } = response
      initPendo(this.get('id'), trackingData)

      return response
    }

    const identifyPostHogWithTrackingData = response => {
      const { body: { data: trackingData } } = response
      identifyPostHogUser(trackingData)
      return response
    }

    const redirect = () => {
      const returnUrl = WebStorageService.getItem(WebStorageKey.RETURN_AFTER_LOGIN)
      const decision = getPostLoginRedirectDecision({
        context: RedirectContext.LOGIN,
        isMeinBuero: isMeinbuero,
        returnAfterLogin: returnUrl,
        defaultPath: opts.path,
        noRedirect: opts.noRedirect,
      })

      if (!decision.blockRedirect && decision.target) {
        navigate(decision.target, { replace: true })
      }
      WebStorageService.removeItem(WebStorageKey.RETURN_AFTER_LOGIN)
      this.setLoginInProgress(false)
    }

    const getEmailServiceStatus = () => {
      return request(settingsConfig.getEmailServiceSetup, { auth: true, isConcurrent: true })
    }

    const updateImapOnLogin = ({ body: { data } }) => {
      store.dispatch(setEmailServiceStatus(data))
      data.useSyncEmailAccounts && reduxStore.dispatch(updateImapEmails())
      return null
    }

    const updateGoogleTag = () => {
      if (this.get('gaClientIdNeeded') === true || attrs.data.ga) {
        setTimeout(() => {
          let gaClientId = attrs.data.ga
          if (gaClientId) {
            gaClientId = ENV.product === 'orgamax' ? Cookies.get('_ga') : Cookies.get('_gaclientid')
          }
          if (gaClientId) {
            request(settingsConfig.account, {
              method: 'POST',
              isConcurrent: true,
              auth: true,
              data: {
                gaClientId,
              },
            })
          } else {
            // TODO REMOVE NEXT: Temporary console log because we cannot test in development
            console.log('should send gtag but found none')
          }
        }
        , 1000)
      } else {
        // TODO REMOVE NEXT: Temporary console log because we cannot test in development
        console.log('gaClientId known')
      }
    }

    const cleanup = () => {
      this._loginPromise = null
    }

    /**
     * Terminal error handler: never rethrows, so the login always ends with a navigation
     * decision. A rethrow here used to leave the caller (finalize-token, approve) on a bare
     * loader forever, because none of them catches.
     *
     * Because of that, login() resolves in both cases and callers can no longer use a rejection
     * to detect failure - they have to check the resolved `success` flag before doing anything
     * that would override the navigation decided here.
     */
    const onBootstrapError = (error) => {
      captureBootstrapError('login', error)
      this.setLoginInProgress(false)

      const status = error?.statusCode ?? error?.response?.status
      if (status === 401 || status === 403) {
        // the token cannot bootstrap at all, so any protected target would bounce right back.
        // ignoreReferrer prevents logout() from storing the current loader route as return target
        toast.showToast({ type: 'error', message: lang.loginBootstrapAuthErrorMessage })
        this.logout(true)
        return { success: false, error }
      }

      toast.showToast({ type: 'error', message: lang.loginBootstrapErrorMessage })
      WebStorageService.removeItem(WebStorageKey.RETURN_AFTER_LOGIN)
      navigate('/dashboard', { replace: true })

      return { success: false, error }
    }

    // the server keeps provisioning after our request timed out, so an immediate retry would
    // run into the same lock - retry out of band instead, through the already catching thunk
    const scheduleTenantSettingRetry = (delays = [5000, 15000, 45000]) => {
      const [delay, ...rest] = delays
      if (delay === undefined) return

      setTimeout(async() => {
        await store.dispatch(fetchTenantSetting())
        if (!store.getState().tenant.settings.error) return

        if (rest.length) {
          scheduleTenantSettingRetry(rest)
        } else {
          toast.showToast({ type: 'error', message: lang.tenantSettingsDegradedMessage })
        }
      }, delay)
    }

    // Run the tenant data fetches in parallel. Only tenantInfo is critical - it is the sole
    // source of vatCodeList/subscriptions/isDemo and has no usable reducer default. All others
    // have safe defaults and are refetched later, so they must not be able to block the login.
    const fetchAllLoginData = () => runBootstrapSteps([
      {
        name: 'tenantInfo',
        critical: true,
        retries: 1,
        run: () => this.getTenantInfo().then(setTenantInfo),
      },
      { name: 'tenantSetting', run: () => this.getTenantSetting().then(setTenantSetting) },
      { name: 'tenantUserSetting', run: () => this.getTenantUserSetting().then(setTenantUserSetting) },
      { name: 'emailStatus', run: () => getEmailServiceStatus().then(updateImapOnLogin) },
      {
        name: 'tracking',
        run: () => getTrackingData()
          .then(sendTrackingDataToPendo)
          .then(identifyPostHogWithTrackingData),
      },
    ]).then(({ failures, criticalFailures }) => {
      // not chained behind tracking anymore: a failing tracking request used to skip this silently
      updateGoogleTag()

      if (failures.some(failure => failure.name === 'tenantSetting')) {
        scheduleTenantSettingRetry()
      }

      if (criticalFailures.length) {
        const error = new Error(`login bootstrap failed: ${criticalFailures.map(f => f.name).join(', ')}`)
        error.isBootstrapFailure = true
        error.statusCode = criticalFailures[0].error?.statusCode
        throw error
      }
    })

    const updateSubscription = () => {
      return updateSubscriptionDetails(true, true)
    }

    // Store the promise to prevent concurrent logins
    this._loginPromise = Promise.resolve()
      .then(loginUser)
      .then(() => withTimeout(fetchAllLoginData(), BOOTSTRAP_CEILING_MS, 'fetchAllLoginData'))
      .then(() => withTimeout(updateSubscription(), BOOTSTRAP_CEILING_MS, 'updateSubscription'))
      .then(redirect)
      .then(() => ({ success: true }))
      .catch(onBootstrapError)
      // finally, not then: otherwise _loginPromise leaks on the error path and every later
      // login() call would return the old, already rejected promise
      .finally(cleanup)

    return this._loginPromise
  },

  logout: function(ignoreReferrer) {
    try {
      CelloService?.shutdown()
      reduxStore.dispatch(cleanupImapEvents())
      clearTimeout(this._loginInProgressTimer)
      this._loginInProgressTimer = null
      const loginData = { loggedIn: false, token: '', loginInProgress: false }
      this.set(loginData)
      reduxStore.dispatch(updateUserData(loginData))
      reduxStore.dispatch(resetDmsTokenSession())
      window.localStorage.removeItem('user')
      window.localStorage.removeItem(WebStorageKey.READ_ONLY_CONFIRMED)
      window.localStorage.removeItem(WebStorageKey.HIDE_X_INVOICE_MODAL)
      window.localStorage.removeItem(WebStorageKey.DANGLING_SUBSCRIPTION_LATER)
      window.localStorage.removeItem(WebStorageKey.OPEN_DASHBOARD_CLIENT_INVITATIONS)
      window.localStorage.removeItem(WebStorageKey.NO_ROLE_ASSIGNED_CONFIRMED)
      window.localStorage.removeItem(WebStorageKey.IS_LOCKED_CONFIRMED)
      window.localStorage.removeItem(WebStorageKey.SHOPPING_CART)
      window.localStorage.removeItem(WebStorageKey.USER_SETTINGS)

      reduxStore.dispatch(resetFiltersForAllLists())

      // never store a loader-only route: redirect() would send the user right back into it
      if (!ignoreReferrer && !isNonReturnableRoute(document.location.pathname)) {
        if (document.location.pathname.startsWith('/shop')) {
          WebStorageService.removeItem(WebStorageKey.RETURN_AFTER_LOGIN)
        } else {
          WebStorageService.setItem(WebStorageKey.RETURN_AFTER_LOGIN, document.location.pathname)
        }
      }
    } finally {
      resetPostHogUser()
      navigate('/login', { replace: true })
    }
  },

  refreshToken: function() {
    return request(settingsConfig.refreshToken, {
      auth: true,
      isConcurrent: true,
      method: 'POST',
    })
      .then(response => {
        this.set({ token: response.body.data.token })
        this.setDataFromJwt()
      })
  },

  getDefaultVat() {
    return this.getDefaultVatByDate()
  },
  getDefaultVatByDate(dateAsDate) {
    const store = require('@/redux/store').default
    const storeState = store.getState()
    const taxRateCountryCodes = storeState.tenant.settings.taxRateCountryCodes
    const countryCode = taxRateCountryCodes.includes(globalConfig.defaultCountryAsISO2) ? globalConfig.defaultCountryAsISO2 : globalConfig.countryCodeATasISO2
    const vatList = this.getVatListByDate(dateAsDate, { countryCode })
    return vatList.find(vat => vat.isDefault === true) || 0
  },
  getVatList(dateAsDate, options) {
    const store = require('@/redux/store').default
    const storeState = store.getState()
    const taxRateCountryCodes = storeState.tenant.settings.taxRateCountryCodes
    const countryCode = taxRateCountryCodes.includes(globalConfig.defaultCountryAsISO2) ? globalConfig.defaultCountryAsISO2 : globalConfig.countryCodeATasISO2
    const defaults = this.getVatListByDate(dateAsDate, { countryCode })
    const workingVatCodeList = getVatCodeList(this).filter(v => taxRateCountryCodes.includes(v.countryCode))

    return workingVatCodeList.reduce((result, vat) => {
      vat.datePeriodIsValid = vatDatePeriodisValid(dateAsDate, vat.validFrom, vat.validTo)
      vat.isCurrent = false
      if (defaults.find(item =>
        item.vatCode === vat.vatCode
        && item.validFrom === vat.validFrom
        && item.validTo === vat.validTo
        && item.countryCode === vat.countryCode)) {
        vat.isCurrent = true
      }

      const dateInfo = {
        validFrom: vat.validFrom,
        validTo: vat.validTo,
        isValid: vat.datePeriodIsValid,
        countryCode: vat.countryCode,
      }
      const existing = result.findIndex(item => item.vatCode === vat.vatCode && item.value === vat.value)
      if (existing >= 0) {
        result[existing].isCurrent = result[existing].isCurrent || vat.isCurrent
        result[existing].dates.push(dateInfo)
      } else {
        result.push({
          name: vat.name,
          value: vat.value,
          vatCode: vat.vatCode,
          isDefault: vat.isDefault,
          isCurrent: vat.isCurrent,
          dates: [dateInfo],
        })
      }
      return result
    }, [])
  },
  getValidVatList(dateAsDate, options) {
    return this.getVatList(dateAsDate, options)
      .filter(v => v.dates.some(e => e.isValid))
  },
  getVatListByDate(dateAsDate, options) {
    const countryCode = getCountryCodeOrDefault(options)
    return filterVatListByDateAndCountry(getVatCodeList(this), dateAsDate, countryCode)
  },
  getVATTransition(ofDate, countryCode) {
    const current = this.getVatListByDate(ofDate, countryCode)
    const list = this.getVatList({ countryCode })
    const result = {}
    current.forEach(item => {
      const vatList = list.filter(vat => vat.vatCode === item.vatCode)
      vatList.forEach(elem => {
        result[`${elem.value}`] = item
      })
    })
    return result
  },
  allowVatNotification() {
    const settings = WebStorageService.getItem(WebStorageKey.USER_SETTINGS) || defaultUserSettings
    return settings.displayVatNotification
  },
  disableVatNotification() {
    const settings = WebStorageService.getItem(WebStorageKey.USER_SETTINGS) || defaultUserSettings
    WebStorageService.setItem(WebStorageKey.USER_SETTINGS, { ...settings, displayVatNotification: false })
  },
})

const getCountryCodeOrDefault = (options) => {
  const { countryCode = globalConfig.defaultCountryAsISO2 } = options || {}

  return countryCode
}

const getVatCodeList = (userInstance) => {
  return userInstance?.vatCodeList?.map(elm => ({ ...elm })) || []
}
